import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * "About the event" — the «Γιατί Δευτέρα;» copy and the four themes.
 *
 * Shared by the app's home screen and the microsite, which had drifted badly:
 * the microsite rendered no CMS content at all, so the copy the client keeps
 * revising existed on one surface only.
 *
 * Everything comes from `content_blocks`. The four themes were a hardcoded
 * array in the app while `track_*` blocks sat in the CMS unused — so the
 * organisers could edit them and see nothing change.
 */

const TRACK_KEYS = ['track_future_talks', 'track_masterclasses', 'track_experience', 'track_music'];

/**
 * `large` is the website: more room, so bigger and brighter copy, as in the
 * design the client sent. The app's home screen keeps the compact size.
 */
export default function EventAbout({ blocks = {}, className = '', large = false }) {
  const { t } = useTranslation();

  const why = blocks.why_monday;
  const tracks = TRACK_KEYS.map((key) => blocks[key]).filter((b) => b && (b.title || b.body));

  return (
    <div className={className}>
      <div className="flex items-center gap-3 mb-4">
        <div className="h-px flex-1 bg-white/10" />
        <span className="label-tag text-white/40 text-[10px]">{t('about.sectionLabel')}</span>
        <div className="h-px flex-1 bg-white/10" />
      </div>

      <div className="mb-6">
        <div className="border-l-2 border-brand pl-4 mb-4">
          <h2 className="font-heading font-extrabold uppercase text-2xl text-white leading-tight">
            {why?.title || t('home.whyTitle')}
          </h2>
        </div>
        {/* A blank line in the CMS text starts a new paragraph; a single one breaks the line. */}
        <div
          className={`font-body whitespace-pre-line space-y-3 ${
            large ? 'text-white/85 text-base sm:text-lg leading-relaxed' : 'text-white/60 text-sm leading-relaxed'
          }`}
        >
          {String(why?.body || t('home.whyBody'))
            .split(/\n\s*\n/)
            .map((para, i) => (
              <p key={i}>{para.trim()}</p>
            ))}
        </div>
        {/*
          The block's own image, and nothing at all when there is none. This
          once pointed at a hardcoded path that never existed in the bundle, so
          every install showed a broken-image placeholder under the copy.
        */}
        {why?.image_url && (
          <div className="mt-4">
            <img src={why.image_url} alt="" className="w-full h-auto rounded-lg" loading="lazy" />
          </div>
        )}
      </div>

      {/*
        The four themes: what the day is made of, not a menu.
        
        Two problems to solve at once. As bordered cards they carried the same
        affordance as everything on the screen that navigates, so people tapped
        them and nothing happened. Stacked one per row they were honest but
        took four screenfuls of scroll to say four short things.
        
        A two-column grid keeps them to two compact rows, and the affordance
        comes off by removing the box entirely: the green rule and the number
        do the structuring instead. Numbering also says "list of four" at a
        glance, which a grid of identical panels does not.
      */}
      {tracks.length > 0 && (
        <dl className={`grid grid-cols-2 gap-x-4 gap-y-5 ${large ? 'sm:grid-cols-4 sm:gap-x-6 mt-8' : ''}`}>
          {tracks.map((track, i) => (
            <div key={track.key} className="relative pl-3">
              <span
                aria-hidden="true"
                className="absolute left-0 top-1 bottom-1 w-px bg-brand/40"
              />
              <dt className={`label-tag text-brand leading-tight mb-1 ${large ? 'text-xs' : 'text-[10px]'}`}>
                <span className="text-white/25 mr-1">{String(i + 1).padStart(2, '0')}</span>
                {track.title}
              </dt>
              <dd className={`leading-snug font-body ${large ? 'text-white/60 text-sm' : 'text-white/50 text-[11px]'}`}>{track.body}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
